import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Button, Card, Col, Dropdown, Form, Row, Table, Spinner,
} from "react-bootstrap";
import {
  FaFileExcel, FaEye, FaMoneyBillWave, FaTags, FaLayerGroup,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
} from "recharts";
import axiosInstance from "../../axiosInstance";
import AdLayout from "../../components/AdLayout";
import { Pagination } from "../../components/ui";
import ContactOrderReview from "../../components/orders/ContactOrderReview";
import ContactOrderVatReview from "../../components/orders/ContactOrderVatReview";
import { ORDER_VAT_LINE_ID } from "../../libs/customerCreatePermission";
import ReportSummaryCard from "./components/ReportSummaryCard";
import PeriodCompareLineChart from "./components/PeriodCompareLineChart";
import { PERIOD_OPTIONS, buildPeriodParams, getPeriodLabel } from "./utils/periodFilter";
import { buildChips, countActiveChips, formatDateChip, resolveOptionLabel } from "./utils/accountingFilterUtils";
import AccountingFilterSection from "./components/AccountingFilterSection";
import AccountingActiveFilters from "./components/AccountingActiveFilters";
import { formatVND as fmtVND } from "./utils/moneyFormat";
import "./accountingFilters.css";
import "./accountingResponsive.css";

const CATEGORY_CARD_COLORS = ["#fd7e14", "#dc3545", "#e83e8c", "#ffc107", "#d63384"];
const LINE_CARD_COLORS = ["#6f42c1", "#0d6efd", "#20c997", "#6610f2", "#0dcaf0"];
const PIE_COLORS = ["#fd7e14", "#0d6efd", "#20c997", "#dc3545", "#6f42c1", "#ffc107", "#e83e8c", "#6610f2"];
const PAGE_SIZE = 20;
const unwrap = (res) => res?.data?.data ?? res?.data ?? {};
const DEFAULT_PERIOD = "MONTH";
const isCanceled = (err) =>
  err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.name === "AbortError";

const buildDefaultFilters = () => ({
  categoryId: "",
  lineId: "",
  period: DEFAULT_PERIOD,
  ...buildPeriodParams(DEFAULT_PERIOD),
});

// Chỉ nhận các khóa hợp lệ từ initialFilters (điều hướng từ Dashboard).
const sanitizeInitial = (init) => {
  if (!init) return {};
  const out = {};
  ["categoryId", "lineId", "period", "dateFrom", "dateTo"].forEach((k) => {
    if (init[k] != null && init[k] !== "") out[k] = String(init[k]);
  });
  return out;
};

const ExpenseList = ({ embedded = false, initialFilters = null }) => {
  const navigate = useNavigate();
  const buildInitialFilters = () => ({ ...buildDefaultFilters(), ...sanitizeInitial(initialFilters) });
  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  // { type: "total" } | { type: "category", id, name } | { type: "line", id, name }
  const [chartMetric, setChartMetric] = useState({ type: "total" });
  const [exporting, setExporting] = useState(false);

  const [categories, setCategories] = useState([]);
  const [lines, setLines] = useState([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initial = useMemo(() => buildInitialFilters(), []);
  const [filterCategory, setFilterCategory] = useState(initial.categoryId || "");
  const [filterLine, setFilterLine] = useState(initial.lineId || "");
  const [filterFrom, setFilterFrom] = useState(initial.dateFrom || "");
  const [filterTo, setFilterTo] = useState(initial.dateTo || "");
  const [period, setPeriod] = useState(initial.period || DEFAULT_PERIOD);
  const [appliedFilters, setAppliedFilters] = useState(initial);

  const [totals, setTotals] = useState(null);
  const [trend, setTrend] = useState([]);
  const [scopedTrend, setScopedTrend] = useState([]);
  const [scopedTrendLoading, setScopedTrendLoading] = useState(false);
  const [contactReviewData, setContactReviewData] = useState(null);

  const fetchSeqRef = useRef(0);
  const abortRef = useRef(null);
  const scopedTrendAbortRef = useRef(null);
  const filtersRef = useRef(appliedFilters);
  filtersRef.current = appliedFilters;

  const chipDefs = useMemo(() => [
    {
      key: "period",
      label: "Thời gian",
      isActive: (v) => v && v !== DEFAULT_PERIOD,
      formatValue: (v, applied) => (
        v === "CUSTOM"
          ? formatDateChip(applied.dateFrom, applied.dateTo)
          : PERIOD_OPTIONS.find((o) => o.value === v)?.label || v
      ),
    },
    {
      key: "categoryId",
      label: "Danh mục chi",
      isActive: (v) => Boolean(v),
      formatValue: (v) => resolveOptionLabel(v, categories, "id", ["name"]),
    },
    {
      key: "lineId",
      label: "Line KD",
      isActive: (v) => Boolean(v),
      formatValue: (v) => resolveOptionLabel(v, lines),
    },
  ], [categories, lines]);

  const activeChips = useMemo(
    () => buildChips(appliedFilters, chipDefs),
    [appliedFilters, chipDefs]
  );

  const buildApiParams = (filters, p = 0) => {
    const params = {
      page: p,
      size: PAGE_SIZE,
      period: filters.period || DEFAULT_PERIOD,
    };
    if (filters.categoryId) params.categoryId = filters.categoryId;
    if (filters.lineId) params.lineId = filters.lineId;
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    return params;
  };

  // Params cho bảng chi tiết, có gắn thêm drill-down theo hạng mục / line đang chọn.
  const buildTableParams = (filters, p, metric) => {
    const params = buildApiParams(filters, p);
    if (metric?.type === "category" && metric.id != null) params.categoryId = metric.id;
    else if (metric?.type === "line" && metric.id != null) params.lineId = metric.id;
    return params;
  };

  const fetchDropdowns = useCallback(async () => {
    try {
      const [cRes, lRes] = await Promise.all([
        axiosInstance.get("/api/accounting/expense-categories/tree").catch(() => null),
        axiosInstance.get("/api/accounting/expenses/lines").catch(() => null),
      ]);
      const catData = unwrap(cRes);
      const lineData = unwrap(lRes);
      // Tree roots = danh mục Level 1
      if (catData) setCategories(Array.isArray(catData) ? catData : []);
      if (lineData) setLines(Array.isArray(lineData) ? lineData : []);
    } catch (_) { /* silent */ }
  }, []);

  // Chỉ tải lại bảng chi tiết (drill-down / phân trang) — giữ nguyên KPI & chart tổng.
  const fetchTable = useCallback(async (p, filters, metric) => {
    const seq = ++fetchSeqRef.current;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const params = buildTableParams(filters, p, metric);
      const res = await axiosInstance.get("/api/accounting/expenses", { params, signal: controller.signal });
      if (seq !== fetchSeqRef.current) return;
      const body = unwrap(res);
      setRows(body.content || []);
      setTotalPages(body.totalPages || 0);
      setTotalElements(body.totalElements || 0);
      setPage(p);
    } catch (err) {
      if (isCanceled(err) || seq !== fetchSeqRef.current) return;
      toast.error("Không thể tải dữ liệu chi phí");
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, []);

  const fetchData = useCallback(async (p = 0, filters, metric = { type: "total" }) => {
    const next = filters || filtersRef.current;
    const seq = ++fetchSeqRef.current;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const tableParams = buildTableParams(next, p, metric);
      const listParams = buildApiParams(next, 0);
      delete listParams.page;
      delete listParams.size;
      const [res, totalsRes, trendRes] = await Promise.all([
        axiosInstance.get("/api/accounting/expenses", { params: tableParams, signal: controller.signal }),
        axiosInstance.get("/api/accounting/expenses/summary/totals", { params: listParams, signal: controller.signal }),
        axiosInstance.get("/api/accounting/expenses/trend", { params: listParams, signal: controller.signal }),
      ]);
      if (seq !== fetchSeqRef.current) return;
      const body = unwrap(res);
      setRows(body.content || []);
      setTotalPages(body.totalPages || 0);
      setTotalElements(body.totalElements || 0);
      setTotals(unwrap(totalsRes));
      const trendBody = unwrap(trendRes);
      setTrend(Array.isArray(trendBody) ? trendBody : []);
      setPage(p);
    } catch (err) {
      if (isCanceled(err) || seq !== fetchSeqRef.current) return;
      toast.error("Không thể tải dữ liệu chi phí");
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDropdowns();
    const defaults = buildInitialFilters();
    setAppliedFilters(defaults);
    syncDraft(defaults);
    fetchData(0, defaults);
    return () => { if (abortRef.current) abortRef.current.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (chartMetric.type === "total") return undefined;
    if (scopedTrendAbortRef.current) scopedTrendAbortRef.current.abort();
    const controller = new AbortController();
    scopedTrendAbortRef.current = controller;
    setScopedTrendLoading(true);
    const params = buildApiParams(appliedFilters, 0);
    delete params.page;
    delete params.size;
    if (chartMetric.type === "category") params.categoryId = chartMetric.id;
    if (chartMetric.type === "line") params.lineId = chartMetric.id;
    axiosInstance.get("/api/accounting/expenses/trend", { params, signal: controller.signal })
      .then((res) => {
        const body = unwrap(res);
        setScopedTrend(Array.isArray(body) ? body : []);
      })
      .catch((err) => {
        if (isCanceled(err)) return;
        toast.error("Không thể tải biểu đồ");
      })
      .finally(() => setScopedTrendLoading(false));
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartMetric, appliedFilters]);

  const applyFilters = (next) => {
    setAppliedFilters(next);
    filtersRef.current = next;
    setChartMetric({ type: "total" });
    setTotals(null);
    setTrend([]);
    setRows([]);
    fetchData(0, next, { type: "total" });
  };

  // Chọn KPI (tổng / hạng mục / line): đổi chart và lọc lại bảng chi tiết cho đúng mục.
  const selectMetric = (metric) => {
    setChartMetric(metric);
    fetchTable(0, appliedFilters, metric);
  };

  const syncDraft = (filters) => {
    setFilterCategory(filters.categoryId || "");
    setFilterLine(filters.lineId || "");
    setPeriod(filters.period || DEFAULT_PERIOD);
    setFilterFrom(filters.dateFrom || "");
    setFilterTo(filters.dateTo || "");
  };

  const buildNextFilters = () => {
    const range = period === "CUSTOM"
      ? { dateFrom: filterFrom, dateTo: filterTo }
      : buildPeriodParams(period);
    return {
      categoryId: filterCategory,
      lineId: filterLine,
      period,
      dateFrom: range.dateFrom || "",
      dateTo: range.dateTo || "",
    };
  };

  const handleSearch = () => applyFilters(buildNextFilters());
  const handleReset = () => {
    const defaults = buildDefaultFilters();
    syncDraft(defaults);
    applyFilters(defaults);
  };
  const handleRemoveChip = (key) => {
    const base = { ...appliedFilters };
    if (key === "period") {
      const defaults = buildDefaultFilters();
      Object.assign(base, defaults);
      base.categoryId = appliedFilters.categoryId;
      base.lineId = appliedFilters.lineId;
    } else {
      base[key] = "";
    }
    syncDraft(base);
    applyFilters(base);
  };

  const handleViewDetail = (r) => {
    if (r.salarySheetId) {
      navigate(`/ke-toan/nhan-su?tab=salary&sheetId=${r.salarySheetId}`);
      return;
    }
    if (r.invoiceContactId && Number(r.invoiceContactId) > 0) {
      const isVat = Number(r.lineId) === ORDER_VAT_LINE_ID;
      setContactReviewData({ id: r.invoiceContactId, lineId: r.lineId, isVat });
      return;
    }
    toast.warn("Phiếu không có liên kết đối soát.");
  };

  const exportExcel = async (mode = "L1") => {
    setExporting(true);
    try {
      const params = buildApiParams(appliedFilters, 0);
      delete params.page;
      delete params.size;
      params.mode = mode;
      const res = await axiosInstance.get("/api/accounting/expenses/export", {
        params,
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = mode === "DETAIL" ? "ChiPhi_ChiTiet.xlsx" : "ChiPhi_Level1.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (_) {
      toast.error("Xuất Excel thất bại");
    } finally {
      setExporting(false);
    }
  };


  const periodLabel = getPeriodLabel(appliedFilters.period);
  const rangeHint = appliedFilters.dateFrom && appliedFilters.dateTo
    ? `${appliedFilters.dateFrom} → ${appliedFilters.dateTo}`
    : "";
  const byCategory = totals?.byCategory || [];
  const byLine = totals?.byLine || [];

  // Không đặt key `percent` trên data — Recharts dùng `percent` (0–1) cho label;
  // nếu trùng tên với % từ API (0–100) sẽ bị nhân 100 lần (vd. 100% → 10000%).
  const pieCategory = useMemo(
    () => byCategory
      .filter((c) => Number(c.amount || 0) > 0)
      .map((c) => ({
        name: c.categoryName || "Không danh mục",
        value: Number(c.amount || 0),
        sharePct: Number(c.percent || 0),
      })),
    [byCategory]
  );
  const pieLine = useMemo(
    () => byLine
      .filter((l) => Number(l.amount || 0) > 0)
      .map((l) => ({
        name: l.lineName || "Không line",
        value: Number(l.amount || 0),
        sharePct: Number(l.percent || 0),
      })),
    [byLine]
  );

  const pieSliceLabel = ({ percent }) =>
    `${((Number(percent) || 0) * 100).toFixed(0)}%`;

  const pieLegendFormatter = (value, entry) => {
    const pct = entry?.payload?.sharePct;
    if (pct == null || Number.isNaN(Number(pct))) return value;
    return `${value} (${Number(pct).toFixed(1)}%)`;
  };

  const pieTooltipFormatter = (v, _n, item) => {
    const pct = item?.payload?.sharePct;
    const pctText = pct != null ? ` (${Number(pct).toFixed(1)}%)` : "";
    return [`${fmtVND(v)}${pctText}`, item?.payload?.name || ""];
  };

  const renderPie = (title, data, colors) => (
    <Card className="mb-3 h-100" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
      <Card.Body>
        <h6 className="fw-bold mb-3">{title}</h6>
        {data.length > 0 ? (
          <div className="acc-chart-wrap">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 12, right: 12, bottom: 8, left: 12 }}>
              <Pie
                data={data}
                cx="50%"
                cy="46%"
                innerRadius={52}
                outerRadius={86}
                paddingAngle={2}
                dataKey="value"
                nameKey="name"
                label={pieSliceLabel}
                labelLine={{ strokeWidth: 1 }}
              >
                {data.map((_, i) => (
                  <Cell key={`slice-${i}`} fill={colors[i % colors.length]} />
                ))}
              </Pie>
              <Tooltip formatter={pieTooltipFormatter} />
              <Legend
                verticalAlign="bottom"
                formatter={pieLegendFormatter}
                wrapperStyle={{ paddingTop: 8, fontSize: 12 }}
              />
            </PieChart>
          </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-center text-muted py-5">Không có dữ liệu</div>
        )}
      </Card.Body>
    </Card>
  );

  const body = (
    <div className={embedded ? "" : "acc-page"}>
      <Card className="mb-3 acc-filter-card">
        <Card.Body className="p-0">
          <AccountingFilterSection
            title={!embedded ? "Báo cáo Chi phí" : undefined}
            open={filterOpen}
            onToggle={setFilterOpen}
            onSubmit={handleSearch}
            onReset={handleReset}
            activeCount={countActiveChips(activeChips)}
            toolbarRight={(
              <Dropdown>
                <Dropdown.Toggle variant="success" size="sm" disabled={loading || exporting}>
                  <FaFileExcel className="me-1" />
                  {exporting ? "Đang xuất…" : "Xuất Excel"}
                </Dropdown.Toggle>
                <Dropdown.Menu align="end" renderOnMount>
                  <Dropdown.Item onClick={() => exportExcel("L1")}>Xuất theo Level 1</Dropdown.Item>
                  <Dropdown.Item onClick={() => exportExcel("DETAIL")}>Xuất chi tiết</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          >
            <Row className="g-2 align-items-end acc-filter-grid">
              <Col>
                <Form.Label className="small mb-1">Thời gian</Form.Label>
                <Form.Select size="sm" value={period} onChange={(e) => setPeriod(e.target.value)}>
                  {PERIOD_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Form.Select>
              </Col>
              {period === "CUSTOM" && (
                <>
                  <Col>
                    <Form.Label className="small mb-1">Từ ngày</Form.Label>
                    <Form.Control type="date" size="sm" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
                  </Col>
                  <Col>
                    <Form.Label className="small mb-1">Đến ngày</Form.Label>
                    <Form.Control type="date" size="sm" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
                  </Col>
                </>
              )}
              <Col>
                <Form.Label className="small mb-1">Danh mục chi</Form.Label>
                <Form.Select size="sm" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                  <option value="">Tất cả</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col>
                <Form.Label className="small mb-1">Line KD</Form.Label>
                <Form.Select size="sm" value={filterLine} onChange={(e) => setFilterLine(e.target.value)}>
                  <option value="">Tất cả</option>
                  {lines.map((l) => (
                    <option key={l.id} value={l.id}>{l.name || l.lineName}</option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          </AccountingFilterSection>
          <AccountingActiveFilters chips={activeChips} onRemove={handleRemoveChip} onClearAll={handleReset} />
        </Card.Body>
      </Card>

      {rangeHint && (
        <div className="text-muted small mb-2">
          Kỳ báo cáo: <strong>{periodLabel}</strong> ({rangeHint})
        </div>
      )}

      <div className="acc-summary-grid--equal">
        <div className="acc-summary-card-wrap">
          <ReportSummaryCard
            title={`Tổng chi phí`}
            value={totals?.totalAmount}
            color="#dc3545"
            icon={FaMoneyBillWave}
            loading={loading}
            compact
            onClick={() => selectMetric({ type: "total" })}
            active={chartMetric.type === "total"}
          />
        </div>
        {byCategory.map((c, idx) => (
          <div key={`cat-${c.categoryId ?? c.categoryName ?? idx}`} className="acc-summary-card-wrap">
            <ReportSummaryCard
              title={c.categoryName || "Không danh mục"}
              value={c.amount}
              color={CATEGORY_CARD_COLORS[idx % CATEGORY_CARD_COLORS.length]}
              icon={FaTags}
              loading={loading}
              compact
              onClick={() => selectMetric({ type: "category", id: c.categoryId, name: c.categoryName || "Không danh mục" })}
              active={chartMetric.type === "category" && chartMetric.id === c.categoryId}
            />
          </div>
        ))}
        {byLine.map((l, idx) => (
          <div key={`line-${l.lineId ?? l.lineName ?? idx}`} className="acc-summary-card-wrap">
            <ReportSummaryCard
              title={l.lineName || "Không line"}
              value={l.amount}
              color={LINE_CARD_COLORS[idx % LINE_CARD_COLORS.length]}
              icon={FaLayerGroup}
              loading={loading}
              compact
              onClick={() => selectMetric({ type: "line", id: l.lineId, name: l.lineName || "Không line" })}
              active={chartMetric.type === "line" && chartMetric.id === l.lineId}
            />
          </div>
        ))}
      </div>

      {loading ? (
        <Card className="mb-3"><Card.Body className="text-center py-4"><Spinner animation="border" size="sm" className="me-2" />Đang tải…</Card.Body></Card>
      ) : chartMetric.type === "total" ? (
        <>
          <PeriodCompareLineChart
            data={trend}
            title={`Biểu đồ chi phí — ${periodLabel} vs kỳ trước`}
            currentLabel="Kỳ này"
            previousLabel="Kỳ trước"
            currentColor="#dc3545"
          />
          <Row className="g-3">
            <Col md={6}>{renderPie("Cơ cấu chi phí theo danh mục", pieCategory, PIE_COLORS)}</Col>
            <Col md={6}>{renderPie("Cơ cấu chi phí theo Line KD", pieLine, LINE_CARD_COLORS)}</Col>
          </Row>
        </>
      ) : scopedTrendLoading ? (
        <Card className="mb-3"><Card.Body className="text-center py-4"><Spinner animation="border" size="sm" className="me-2" />Đang tải…</Card.Body></Card>
      ) : (
        <PeriodCompareLineChart
          data={scopedTrend}
          title={`Biểu đồ chi phí — ${chartMetric.name} — ${periodLabel} vs kỳ trước`}
          currentLabel="Kỳ này"
          previousLabel="Kỳ trước"
          currentColor="#dc3545"
        />
      )}

      <Card style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : rows.length === 0 ? (
            <div className="text-center py-5 text-muted">Không có dữ liệu chi phí trong kỳ đã chọn</div>
          ) : (
            <>
              <div className="acc-table-wrap">
                <Table striped hover className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 50 }}>STT</th>
                      <th>Đối tượng chi</th>
                      <th>Mã phiếu chi</th>
                      <th className="acc-col-hide-sm">Ngày chi</th>
                      <th>Line KD</th>
                      <th className="acc-col-hide-sm">Danh mục chi</th>
                      <th className="text-end">Số tiền</th>
                      <th className="text-center" style={{ width: 60 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => (
                      <tr key={`${r.id}-${r.lineId}-${idx}`}>
                        <td>{idx + 1 + page * PAGE_SIZE}</td>
                        <td>{r.partnerExpense
                          ? (r.partnerCode || r.partnerName || "—")
                          : (r.partnerName || "—")}</td>
                        <td>{r.voucherCode || "—"}</td>
                        <td className="acc-col-hide-sm">{r.expenseDate || "—"}</td>
                        <td>{r.lineName || "—"}</td>
                        <td className="acc-col-hide-sm">{r.categoryName || "—"}</td>
                        <td className="text-end fw-semibold text-danger">{fmtVND(r.amount)}</td>
                        <td className="text-center">
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-primary"
                            title="Xem chi tiết"
                            onClick={() => handleViewDetail(r)}
                          >
                            <FaEye />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <div className="acc-table-footer">
                <small className="text-muted">Tổng: {totalElements} dòng</small>
                <Pagination
                  hidePaginationWhenSinglePage
                  showItemsPerPage={false}
                  pageData={{ page: { number: page, totalPages, totalElements, size: PAGE_SIZE } }}
                  onPageChange={(p) => !loading && fetchTable(p - 1, appliedFilters, chartMetric)}
                />
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {contactReviewData && !contactReviewData.isVat && (
        <ContactOrderReview
          data={contactReviewData}
          handleSearch={() => setContactReviewData(null)}
          canEdit={false}
          canDelete={false}
          nestedModal
        />
      )}
      {contactReviewData && contactReviewData.isVat && (
        <ContactOrderVatReview
          data={contactReviewData}
          handleSearch={() => setContactReviewData(null)}
          canEdit={false}
          canDelete={false}
          nestedModal
        />
      )}
    </div>
  );

  if (embedded) return body;
  return <AdLayout>{body}</AdLayout>;
};

export default ExpenseList;
